/**
 * Company Search — data access.
 *
 * Every call is a Supabase Edge Function invocation (server-side authorized):
 * lookup_company, get_company_profile, request_company_scan, get_scan_status,
 * and the additive get_signal_feed organization filter for linked signals. No
 * PostgREST table writes and no supabase.rpc, so the client-write gate stays
 * green with zero suppressions.
 *
 * The success envelope is double-nested: supabase.functions.invoke returns
 * `{ data, error }` where `data` is the full Edge body `{ data: <payload> }`,
 * so the typed `T` is `{ data: <payload> }` and callers read `res.data`.
 */

import { supabase } from "@/auth/supabase-client";
import {
  ensureFreshSession,
  SUPABASE_EDGE_TIMEOUT_MS,
  withSupabaseTimeout,
} from "@/auth/session-guard";
import type {
  RawBriefGenerateResponse,
  RawBriefResponse,
  RawCompanyContact,
  RawCompanyContactsResponse,
  RawCompanyPatternRead,
  RawCompanyPatternReadsResponse,
  RawLinkedSignalRow,
  RawLookupActivity,
  RawLookupResponse,
  RawProfileResponse,
  RawScanStatus,
  ScanTarget,
} from "./types";

/** Best-effort extraction of the Edge Function's `{error:{message}}` body. */
async function messageFor(error: unknown): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const parsed = (await ctx.json()) as { error?: { message?: string } };
      if (parsed?.error?.message) return parsed.error.message;
    } catch {
      /* non-JSON error body — fall through to the generic message */
    }
  }
  return error instanceof Error ? error.message : String(error);
}

/** The HTTP status the Edge boundary returned, if any (200/400/401/404/500).
 *  Reads the status preserved by invoke() on rethrow, falling back to the raw
 *  supabase-js error's `context.status`. */
function statusOf(error: unknown): number | undefined {
  const e = error as { status?: number; context?: { status?: number } };
  return e.status ?? e.context?.status;
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  await ensureFreshSession();
  const { data, error } = await withSupabaseTimeout(
    supabase.functions.invoke(name, { body }),
    `${name} request`,
    SUPABASE_EDGE_TIMEOUT_MS,
  );
  if (error) {
    // Preserve the Edge HTTP status on the rethrown error so statusOf() can
    // drive 404/empty classification numerically, not only by message text.
    const wrapped = new Error(`${name}: ${await messageFor(error)}`) as Error & { status?: number };
    const status = statusOf(error);
    if (status !== undefined) wrapped.status = status;
    throw wrapped;
  }
  return data as T;
}

/** lookup_company — resolve a free-text company query to an identity outcome. */
export async function lookupCompany(query: string): Promise<RawLookupResponse> {
  const res = await invoke<{ data: RawLookupResponse }>("lookup_company", { query });
  return res.data;
}

/** Discriminated so the UI can render an honest "no profile on record" state
 *  for a 404 distinctly from a hard error. */
export type ProfileResult =
  | { status: "ok"; profile: RawProfileResponse }
  | { status: "not_found" };

/** get_company_profile — delivery/relationship slice for a resolved org. */
export async function getCompanyProfile(organizationId: string): Promise<ProfileResult> {
  try {
    const res = await invoke<{ data: RawProfileResponse }>("get_company_profile", {
      organization_id: organizationId,
    });
    return { status: "ok", profile: res.data };
  } catch (error) {
    if (statusOf(error) === 404 || /not[ _]?found/i.test(String(error))) {
      return { status: "not_found" };
    }
    throw error;
  }
}

/** request_company_scan (v2) — record/reuse a scan for an org or an unresolved
 *  name. Returns the projectCompanyScan envelope (status, effective_status,
 *  can_cancel, reused, …). */
export async function requestCompanyScan(target: ScanTarget): Promise<RawScanStatus> {
  const res = await invoke<{ data: RawScanStatus }>("request_company_scan", { ...target });
  return res.data;
}

/** get_scan_status (v2, organization-keyed) — the current live/most-recent scan
 *  for a resolved org. A 404 (no scan on record) is an honest null, not an
 *  error, so the request affordance is shown. */
export async function fetchScanStatusByOrg(
  organizationId: string,
): Promise<RawScanStatus | null> {
  try {
    const res = await invoke<{ data: RawScanStatus }>("get_scan_status", {
      organization_id: organizationId,
    });
    return res.data;
  } catch (error) {
    if (statusOf(error) === 404 || /not[ _]?found/i.test(String(error))) return null;
    throw error;
  }
}

/** cancel_company_scan — cancel a pending scan by request_id (requester or admin,
 *  pending-only, enforced server-side). Returns the updated envelope. */
export async function cancelCompanyScan(requestId: string): Promise<RawScanStatus> {
  const res = await invoke<{ data: RawScanStatus }>("cancel_company_scan", {
    request_id: requestId,
  });
  return res.data;
}

/** get_company_pattern_reads — authored pattern reads for a resolved org
 *  (element g). A 404 (org did not resolve) is an honest empty. */
export async function fetchCompanyPatternReads(
  organizationId: string,
): Promise<RawCompanyPatternRead[]> {
  try {
    const res = await invoke<{ data: RawCompanyPatternReadsResponse }>(
      "get_company_pattern_reads",
      { organization_id: organizationId },
    );
    return res.data.pattern_reads ?? [];
  } catch (error) {
    if (statusOf(error) === 404 || /not[ _]?found/i.test(String(error))) return [];
    throw error;
  }
}

/** get_company_lookup_activity — first-visit rails (recents + trending). Limits
 *  default server-side; the client takes the sealed defaults. */
export async function fetchLookupActivity(): Promise<RawLookupActivity> {
  const res = await invoke<{ data: RawLookupActivity }>("get_company_lookup_activity", {});
  return res.data;
}

/** get_signal_feed (organization filter) — a company's linked signals, if any.
 *  The feed returns full rows; we keep the organization-scoped subset. */
export async function fetchLinkedSignals(
  organizationId: string,
): Promise<RawLinkedSignalRow[]> {
  const res = await invoke<{ data: { rows?: RawLinkedSignalRow[] } }>("get_signal_feed", {
    tab: "all",
    limit: 50,
    organization_id: organizationId,
  });
  return res.data.rows ?? [];
}

/** get_company_contacts — internal contacts for a resolved org (element d).
 *  Returns the server's pre-ordered set. A 404 (organization did not resolve)
 *  is treated as an honest empty rather than an error, matching the profile's
 *  not-found handling; real failures still throw and drive the error state. */
export async function fetchCompanyContacts(
  organizationId: string,
): Promise<RawCompanyContact[]> {
  try {
    const res = await invoke<{ data: RawCompanyContactsResponse }>("get_company_contacts", {
      organization_id: organizationId,
    });
    return res.data.contacts ?? [];
  } catch (error) {
    if (statusOf(error) === 404 || /not[ _]?found/i.test(String(error))) return [];
    throw error;
  }
}

/** claim_signal — inline claim of an unclaimed company signal (annotation 1).
 *  Server-authorized Edge mutation (requireClaimableSignal); the client never
 *  writes ownership directly, so the client-write gate stays green. Returns the
 *  recorded claim event (201). */
export async function claimSignal(signalId: string): Promise<void> {
  await invoke<{ data: { event: unknown } }>("claim_signal", { signal_id: signalId });
}

/** generate-company-brief: the CIB Layer 1 request control.
 *
 * One call serves both outcomes the deployed contract offers: a 200 with the
 * cached research body when this company was generated inside the 14 day TTL,
 * or a 202 acknowledgment when a generation is now running in the background
 * (freshly started, or the caller folded into an existing one). No deployed
 * contract maps a company to an existing brief, so this call is also the only
 * way to discover a cached one.
 *
 * v1 sends the default parameter profile: stage, focus, and parameters are
 * omitted so the server's defaults apply and every request stays cacheable.
 * company_domain is carried by no Company Search contract in this tree, so it
 * is omitted rather than guessed. Resolved companies use the server's
 * org:<uuid> cache key; unresolved names use its normalized name:<company>
 * fallback. The brief service therefore remains available even when BDSE has
 * no existing relationship record for the searched company. */
export async function generateCompanyBrief(input: {
  companyName: string;
  organizationId: string | null;
  regenerate: boolean;
}): Promise<RawBriefGenerateResponse> {
  const res = await invoke<{ data: RawBriefGenerateResponse }>(
    "generate-company-brief",
    {
      company_name: input.companyName,
      organization_id: input.organizationId,
      regenerate: input.regenerate,
    },
  );
  return res.data;
}

/** get-company-brief: read a generation by its brief_id. Keyed only by the
 *  id the generate call returned; there is no company keyed read. */
export async function fetchCompanyBrief(briefId: string): Promise<RawBriefResponse> {
  const res = await invoke<{ data: RawBriefResponse }>("get-company-brief", {
    brief_id: briefId,
  });
  return res.data;
}
